import { useQuery } from "@tanstack/react-query";

import { listRoles } from "../api/organization-api";
import type { EnterpriseRole } from "../types";

interface UseRolesReturn {
  roles: readonly EnterpriseRole[];
  isLoading: boolean;
  error: string | null;
}

export const rolesKey = (enterpriseId: string): readonly unknown[] =>
  ["organization", "roles", enterpriseId] as const;

/**
 * The role list for one enterprise.
 *
 * Keyed on the enterprise, so a console that switches enterprise cannot paint
 * the previous one's roles: the answer to a read for enterprise A is never
 * returned for enterprise B.
 */
export function useRoles(enterpriseId: string): UseRolesReturn {
  const query = useQuery({
    queryKey: rolesKey(enterpriseId),
    queryFn: () => listRoles(enterpriseId),
  });

  return {
    roles: query.data ?? [],
    isLoading: query.isPending,
    /* Gated on there being no data, so a failed revalidation after a create or
       delete leaves the painted list alone instead of replacing the whole
       section with a retry card. */
    error: query.isError && query.data === undefined ? "Failed to load roles" : null,
  };
}
